import { array } from 'fp-ts';
import { Identifiable } from '@mews/std';
import { AsyncAction, CommanderThunkAction, WithAccountId } from '@mews-commander/types';
import {
    AccountNoteAddParameters,
    AccountNoteExtent,
    MultipleAccountNoteUpdateParameters,
} from '@mews-commander/data-access';
import { createFetchAction, callApiInChunks } from '@mews-commander/utils';
import {
    ACCOUNT_NOTE_ADD,
    ACCOUNT_NOTE_DELETE,
    ACCOUNT_NOTE_UPDATE,
    getAccountNote,
    setAccountNotes,
} from '../ducks/accountNotes';
import prefix from '../prefix';
import { setProfiles } from '../ducks';

const ACCOUNT_NOTE_FETCH = prefix('ACCOUNT_NOTES_FETCH');

export const addAccountNote =
    ({ accountId, content, data }: AccountNoteAddParameters): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.accountNotes.add({ accountId, data, content }).then((accountNote) => accountNote);

        return dispatch(createFetchAction(ACCOUNT_NOTE_ADD, promise));
    };

export const updateAccountNote =
    ({ accountNoteUpdates }: MultipleAccountNoteUpdateParameters): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.accountNotes
            .update({ accountNoteUpdates })
            .then(({ accountNotes }) => ({ ...accountNotes[0] }));
        return dispatch(createFetchAction(ACCOUNT_NOTE_UPDATE, promise));
    };

export const deleteAccountNote =
    ({ id }: Identifiable): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const accountNote = getAccountNote(getState(), { id });
        const promise = repositories.accountNotes.delete({ accountNoteIds: [id] }).then(() => accountNote);
        return dispatch(createFetchAction(ACCOUNT_NOTE_DELETE, promise));
    };

interface FetchAccountNotesParams {
    accountIds: string[];
    extent?: AccountNoteExtent;
}

export const fetchAccountNotes =
    ({ accountIds, extent = {} }: FetchAccountNotesParams): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = callApiInChunks({
            apiCall: repositories.accountNotes.getAll,
            chunkSize: 1000,
            processChunks: array.map((chunkedIds: string[]) => ({ accountIds: chunkedIds, extent })),
        })(accountIds)
            .then((responses) => ({
                accountNotes: responses.flatMap(({ accountNotes }) => accountNotes ?? []),
                profiles: responses.flatMap(({ profiles }) => profiles ?? []),
            }))
            .then(({ accountNotes, profiles }) => {
                if (accountNotes?.length) {
                    dispatch(setAccountNotes(accountNotes));
                }
                if (profiles?.length) {
                    dispatch(setProfiles(profiles));
                }
            });

        return dispatch(createFetchAction(ACCOUNT_NOTE_FETCH, promise));
    };

export const fetchAccountNotesByAccountId = ({ accountId, extent }: WithAccountId & { extent?: AccountNoteExtent }) =>
    fetchAccountNotes({ accountIds: [accountId], extent });

export const fetchAccountNotesByAccountIds = ({
    accountIds,
    extent,
}: { accountIds: string[] } & { extent?: AccountNoteExtent }) => fetchAccountNotes({ accountIds, extent });
